import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { v4 as uuidv4 } from 'uuid';
// Helpers
import { EventTicketTierDraft } from 'helpers/ticketEvents';
import { ticketTierValidationSchema } from 'helpers/validation';
// Ui
import Modal from 'ui3/Modal/Modal';
import Input from 'ui3/Input/Input';
import Button from 'ui3/Button/Button';
import Text from 'ui3/Text/Text';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Styles
import styles from './AddTierModal.module.scss';

type TierFormValues = {
  label: string;
  price: string;
  capacity: string;
};

const EMPTY_TIER: TierFormValues = { label: '', price: '', capacity: '' };

type AddTierModalProps = {
  open: boolean;
  onClose: () => void;
  onAdd: (tier: EventTicketTierDraft) => void;
};

const AddTierModal = ({
  open,
  onClose,
  onAdd,
}: AddTierModalProps): JSX.Element => {
  const { register, handleSubmit, errors, reset } = useForm<TierFormValues>({
    resolver: yupResolver(ticketTierValidationSchema),
    defaultValues: EMPTY_TIER,
  });

  useEffect(() => {
    if (open) {
      reset(EMPTY_TIER);
    }
  }, [open, reset]);

  const handleAdd = handleSubmit(({ label, price, capacity }) => {
    onAdd({
      id: uuidv4(),
      label: label.trim(),
      price: Number(price),
      capacity: Number(capacity),
    });
    onClose();
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="small"
      showCloseIcon={false}
      className={styles.modal}
    >
      {/* The close button comes last so the modal's focus trap starts on Tier Name; the grid puts it in the header. */}
      <div className={styles.layout}>
        <Text as="h2" variant="subtitle1" className={styles.title}>
          Add Tier
        </Text>
        <form className={styles.root} onSubmit={handleAdd} noValidate>
          <Input
            name="label"
            label="Tier Name"
            placeholder="eg. General Admission"
            ref={register}
            error={errors.label?.message}
          />
          <div className={styles.row}>
            <Input
              name="price"
              label="Price"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              icon="dollarSign"
              fieldSize="medium"
              placeholder="25"
              ref={register}
              error={errors.price?.message}
            />
            <Input
              name="capacity"
              label="Quantity"
              type="number"
              inputMode="numeric"
              step="1"
              min="1"
              fieldSize="medium"
              placeholder="250"
              ref={register}
              error={errors.capacity?.message}
            />
          </div>
          <Button
            type="submit"
            variant="primary"
            size="medium"
            className={styles.submit}
          >
            Add Ticket Tier
          </Button>
        </form>
        <button
          type="button"
          className={styles.closeButton}
          aria-label="Close"
          onClick={onClose}
        >
          <MillionsIcon name="close" size={24} />
        </button>
      </div>
    </Modal>
  );
};

export default AddTierModal;
